import ClickAwayListener from '@mui/material/ClickAwayListener';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import { alpha, CSSObject, styled, Theme } from '@mui/material/styles';
import MarkdownRenderer from '_shared/_views/markdown/markdown-renderer';
import AssistantCitation from 'ai-assistant/citations/assistant-citation';
import rehypeCitationMarkers from 'ai-assistant/citations/_views/citation-markers';
import CitationPreview from 'ai-assistant/citations/_views/citation-preview';
import Citations, {
  citationPanelId,
  citationRegionId,
  citationSummaryId
} from 'ai-assistant/citations/_views/citations';
import AssistantGrounding from 'ai-assistant/grounding/assistant-grounding';
import GroundingNotice from 'ai-assistant/grounding/_views/grounding-notice';
import React from 'react';
import { Components, ExtraProps } from 'react-markdown';

const UNVERIFIED_ORDINAL = '?';

/** WCAG 2.2 SC 2.5.8: the smallest target a pointer should have to hit. */
const MINIMUM_TARGET = 24;

/**
 * A small chip that is a full-size target.
 *
 * The chip stays small so it does not push the line apart; a transparent `::before` centred
 * on it takes the clicks for a 24px square around it. The horizontal margin keeps the
 * centres of two markers in a row, `[[1]][[2]]`, a full target apart, so their targets meet
 * rather than overlap and a tap between them is not a guess.
 */
const markerStyles = (theme: Theme): CSSObject => ({
  position: 'relative',
  font: 'inherit',
  fontSize: theme.typography.pxToRem(10),
  lineHeight: 1,
  verticalAlign: 'super',
  minWidth: 16,
  margin: `0 ${(MINIMUM_TARGET - 16) / 2}px`,
  padding: '1px 4px',
  border: 'none',
  borderRadius: '6px',
  cursor: 'pointer',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: `max(100%, ${MINIMUM_TARGET}px)`,
    height: `max(100%, ${MINIMUM_TARGET}px)`,
    transform: 'translate(-50%, -50%)',
  },
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
  },
});

const Marker = styled('button')(({ theme }) => ({
  ...markerStyles(theme),
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

/** How wide one ordinal in a cluster is, and the comma after it: their sum is a full target. */
const CLUSTER_ORDINAL_WIDTH = 20;
const CLUSTER_SEPARATOR_WIDTH = MINIMUM_TARGET - CLUSTER_ORDINAL_WIDTH;

/**
 * Markers in a row, `[[1]][[2]][[3]]`, drawn as one chip that reads "1, 2, 3" (#5988).
 *
 * Each ordinal in it is still its own button, so each still opens its own source by mouse,
 * keyboard or touch. An ordinal and the comma after it are a full target wide, so the 24px
 * targets of two ordinals meet rather than overlap.
 */
const Cluster = styled('span')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  fontSize: theme.typography.pxToRem(10),
  lineHeight: 1,
  verticalAlign: 'super',
  margin: `0 ${(MINIMUM_TARGET - 16) / 2}px`,
  borderRadius: '6px',
  color: theme.palette.primary.contrastText,
  backgroundColor: theme.palette.primary.main,
}));

const ClusterOrdinal = styled('button')(({ theme }) => ({
  ...markerStyles(theme),
  fontSize: 'inherit',
  verticalAlign: 'baseline',
  minWidth: CLUSTER_ORDINAL_WIDTH,
  margin: 0,
  padding: '1px 0',
  color: 'inherit',
  backgroundColor: 'transparent',
  '&:hover': {
    backgroundColor: theme.palette.primary.dark,
  },
}));

const ClusterSeparator = styled('span')({
  display: 'inline-block',
  width: CLUSTER_SEPARATOR_WIDTH,
});

/**
 * The unverified mark. Black on the warning colour rather than `warning.contrastText`: MUI
 * picks white for light mode's orange at its 3:1 threshold, which 10px text cannot read.
 */
const UnverifiedMarker = styled('button')(({ theme }) => ({
  ...markerStyles(theme),
  color: theme.palette.common.black,
  backgroundColor: theme.palette.warning.main,
  '&:hover': {
    backgroundColor: theme.palette.warning.light,
  },
}));

export const UNVERIFIED_CLAIM_CLASS = 'assistant-unverified-claim';

/** The words an unverified mark is about. Quiet, but not the same as a verified sentence. */
const UnverifiedClaim = styled('span')(({ theme }) => ({
  backgroundColor: alpha(theme.palette.warning.main, 0.1),
  textDecorationLine: 'underline',
  textDecorationStyle: 'dotted',
  textDecorationColor: theme.palette.warning.dark,
  textDecorationThickness: 2,
  textUnderlineOffset: 3,
  ...theme.applyStyles('dark', {
    backgroundColor: alpha(theme.palette.warning.main, 0.16),
    textDecorationColor: theme.palette.warning.main,
  }),
}));

const Explanation = styled(Paper)(({ theme }) => ({
  maxWidth: 260,
  padding: theme.spacing(1, 1.5),
  fontSize: theme.typography.pxToRem(13),
}));

const UNVERIFIED_LABEL = 'This statement is not supported by the source it cited.';

/**
 * A toggletip: the explanation opens on click, tap, Enter or Space, and closes on Escape or
 * on leaving. Screen readers get it as the button's description whether it is open or not -
 * `aria-describedby` reads a hidden element it points at - so nothing depends on a hover
 * `title` that keyboard, touch and magnifier users never see.
 */
const Unverified = () => {
  const explanationId = React.useId();
  const [anchor, setAnchor] = React.useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = React.useState(false);

  return (
    <>
      <ClickAwayListener onClickAway={() => setOpen(false)}>
        <UnverifiedMarker
          ref={setAnchor}
          type="button"
          aria-label="Unverified statement"
          aria-describedby={explanationId}
          aria-expanded={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
          onBlur={() => setOpen(false)}
          onKeyDown={(event) => {
            // An open explanation is what the Escape was aimed at. Left to bubble, it also
            // closed the assistant panel around it. A closed one lets Escape close the panel.
            if (event.key !== 'Escape' || !open) return;
            event.stopPropagation();
            setOpen(false);
          }}
        >
          ?
        </UnverifiedMarker>
      </ClickAwayListener>
      <Popper
        open={open && anchor !== null}
        anchorEl={anchor}
        keepMounted
        placement="top"
        sx={{ zIndex: (theme) => theme.zIndex.tooltip }}
      >
        <Explanation id={explanationId} elevation={4}>
          {UNVERIFIED_LABEL}
        </Explanation>
      </Popper>
    </>
  );
};

/** Module scope, so the plugin list is the same array on every render. */
const CITATION_PLUGINS = [rehypeCitationMarkers];


/** What a marker or a claim inside the answer needs to know about the answer around it. */
type AnswerContext = {
  citationsByOrdinal: Map<number, AssistantCitation>;
  panelScope: string;
  expandedOrdinal?: number;
  revealCitation: (ordinal: number) => void;
};

/**
 * Carries the answer's state to the element overrides, which live at module scope.
 *
 * They have to. react-markdown renders each override as a component, so an override defined
 * inside `CitedAnswer` is a new component type on every render, and React replaced every
 * marker in the answer each time the answer re-rendered - taking a focused marker's focus and
 * an open explanation with it.
 */
const CitedAnswerContext = React.createContext<AnswerContext | undefined>(undefined);

const isUnverified = (marker: string, context: AnswerContext | undefined) =>
  marker === UNVERIFIED_ORDINAL || !context?.citationsByOrdinal.has(Number(marker));

/** The citation a marker opens, or nothing for one the server would not stand behind. */
const citedBy = (marker: string, context: AnswerContext | undefined) =>
  marker === UNVERIFIED_ORDINAL ? undefined : context?.citationsByOrdinal.get(Number(marker));

type SourceMarkerProps = {
  citation: AssistantCitation;
  context: AnswerContext;
  /** A marker on its own is a chip; an ordinal in a cluster shares the cluster's. */
  as: typeof Marker | typeof ClusterOrdinal;
};

/**
 * A control for one source: it opens the source's panel, and shows what it points at while it
 * is hovered or focused (#5987).
 *
 * `describeChild`, because the default writes the title over the marker's `aria-label`: the
 * marker stays named "Source 1: ...", and the preview is its description.
 *
 * The preview is opened and closed here rather than by the Tooltip, so that Escape on an open
 * preview closes the preview and goes no further. The assistant panel closes on any Escape that
 * reaches it, and closed the whole conversation along with the preview the reader dismissed.
 */
const SourceMarker = ({ citation, context, as: Component }: SourceMarkerProps) => {
  const [open, setOpen] = React.useState(false);
  return (
    <Tooltip
      describeChild
      placement="top"
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      title={<CitationPreview citation={citation} />}
    >
      <Component
        type="button"
        aria-label={`Source ${citation.ordinal}: ${citation.sourceLabel}`}
        aria-controls={citationRegionId(context.panelScope, citation.ordinal)}
        aria-expanded={context.expandedOrdinal === citation.ordinal}
        onClick={() => context.revealCitation(citation.ordinal)}
        onKeyDown={(event) => {
          if (event.key !== 'Escape' || !open) return;
          event.stopPropagation();
          setOpen(false);
        }}
      >
        {citation.ordinal}
      </Component>
    </Tooltip>
  );
};

/**
 * Markers that only space separates. The ones with a source to open share one chip; a mark the
 * server would not stand behind keeps its own, after it, because it opens an explanation rather
 * than a source and is drawn in the warning colour. One source alone is a marker as any other.
 */
const MarkerCluster = ({ markers }: { markers: string[] }) => {
  const context = React.useContext(CitedAnswerContext);
  const cited = new Map<number, AssistantCitation>();
  for (const marker of markers) {
    const citation = citedBy(marker, context);
    if (citation) cited.set(citation.ordinal, citation);
  }
  const unverified = markers.filter((marker) => isUnverified(marker, context));
  const citations = [...cited.values()];

  return (
    <>
      {context && citations.length === 1 ? (
        <SourceMarker citation={citations[0]} context={context} as={Marker} />
      ) : null}
      {context && citations.length > 1 ? (
        <Cluster>
          {citations.map((citation, index) => (
            <React.Fragment key={citation.ordinal}>
              <SourceMarker citation={citation} context={context} as={ClusterOrdinal} />
              {index < citations.length - 1 ? <ClusterSeparator aria-hidden="true">,</ClusterSeparator> : null}
            </React.Fragment>
          ))}
        </Cluster>
      ) : null}
      {unverified.map((marker, index) => <Unverified key={`${marker}-${index}`} />)}
    </>
  );
};

const CitationMarker = (props: React.ComponentProps<'sup'> & ExtraProps) => {
  // Only rehypeCitationMarkers sets these, and only after sanitization, so a `sup` in the
  // answer's own text can never stand in for one.
  const marker = props.node?.data?.citationMarker;
  const markers = props.node?.data?.citationCluster ?? (marker === undefined ? undefined : [marker]);
  if (markers === undefined) {
    return <sup>{props.children}</sup>;
  }
  // A marker for a source that is not in the citation list has nothing to open. It is
  // still a claim that had a source, so it reads as unchecked rather than as uncited.
  return <MarkerCluster markers={markers} />;
};

const CitedClaim = (props: React.ComponentProps<'span'> & ExtraProps) => {
  const { node, ...rest } = props;
  const context = React.useContext(CitedAnswerContext);
  // Set by rehypeCitationMarkers on the sentence a marker closes. Only a sentence the server
  // would not stand behind looks any different.
  const claim = node?.data?.citedClaim;
  if (claim === undefined) {
    return <span {...rest} />;
  }
  return claim.some((marker) => isUnverified(marker, context))
    ? <UnverifiedClaim className={UNVERIFIED_CLAIM_CLASS}>{rest.children}</UnverifiedClaim>
    : <>{rest.children}</>;
};

/** Module scope, so every render hands react-markdown the same component types. */
const CITATION_COMPONENTS: Components = { sup: CitationMarker, span: CitedClaim };

type CitedAnswerProps = {
  text: string;
  citations: AssistantCitation[];
  /**
   * What the server said it checked. Optional so a locally-built message - the user's own
   * turn, an optimistic echo - does not have to state a grounding it has no opinion about.
   */
  grounding?: AssistantGrounding;
};

// scrollIntoView is absent in jsdom and in some older embedded browsers; opening the panel and
// moving focus to it are the parts that matter, scrolling to it is a convenience.
const scrollToPanel = (panelScope: string, ordinal: number) =>
  document.getElementById(citationPanelId(panelScope, ordinal))?.scrollIntoView?.({
    behavior: 'smooth',
    block: 'nearest',
  });

/** A marker click still to finish: the panel to focus, and whether it is already open. */
type Reveal = { ordinal: number; alreadyOpen: boolean };

/**
 * An assistant answer and the sources it cites, joined: clicking a marker opens the span
 * that claim came from.
 */
export default function CitedAnswer(props: Readonly<CitedAnswerProps>) {
  const { text, citations, grounding } = props;
  const [expandedOrdinal, setExpandedOrdinal] = React.useState<number | undefined>(undefined);
  const [reveal, setReveal] = React.useState<Reveal | undefined>(undefined);
  // Ordinals are turn-local, and a conversation renders every answer into one document, so
  // the panel ids need something per-answer to stay unique across the page.
  const panelScope = React.useId();

  // After the expansion commits, not in the click handler: until then the panel is still
  // closed. Focus goes to the panel's summary, so a keyboard or screen-reader user lands on
  // what the marker opened, and the browser brings that summary into view as it does.
  React.useEffect(() => {
    if (!reveal) return;
    document.getElementById(citationSummaryId(panelScope, reveal.ordinal))?.focus();
    // A panel that was already open plays no transition, so nothing else will scroll it.
    if (reveal.alreadyOpen) scrollToPanel(panelScope, reveal.ordinal);
  }, [reveal, panelScope]);

  const revealCitation = (ordinal: number) => {
    setExpandedOrdinal(ordinal);
    setReveal({ ordinal, alreadyOpen: expandedOrdinal === ordinal });
  };

  // The excerpt grows in over the panel's transition. Scrolling once it has finished brings
  // the whole excerpt into view, not just the summary above where it was going to be.
  const handleOpened = (ordinal: number) => {
    if (reveal?.ordinal !== ordinal) return;
    scrollToPanel(panelScope, ordinal);
    setReveal(undefined);
  };

  const context: AnswerContext = {
    citationsByOrdinal: new Map(citations.map((citation) => [citation.ordinal, citation])),
    panelScope,
    expandedOrdinal,
    revealCitation,
  };

  return (
    <CitedAnswerContext.Provider value={context}>
      <MarkdownRenderer components={CITATION_COMPONENTS} rehypePlugins={CITATION_PLUGINS}>
        {text}
      </MarkdownRenderer>
      {grounding && <GroundingNotice grounding={grounding} />}
      {citations.length > 0 && (
        <Citations
          sx={{ mt: 2, mb: 1 }}
          citations={citations}
          panelScope={panelScope}
          expandedOrdinal={expandedOrdinal}
          onExpandedOrdinalChange={setExpandedOrdinal}
          onOpened={handleOpened}
        />
      )}
    </CitedAnswerContext.Provider>
  );
}
